import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Spin, DatePicker, Tabs, Table, Select } from 'antd';
import dayjs from 'dayjs';
import { 
  UsergroupAddOutlined,
  PlaySquareOutlined,
  CrownOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { Line, Pie, Area, Column } from '@ant-design/plots';
import { 
  getBasicStats, 
  getCreditStats, 
  getDailyVideoStats,
  getVideoStatusStats,
  getVideoTypeStats,
  getDailyToolUsage,
  getVideoCostStats,
  getAllTools
} from '../../graphql/queries';
import './Dashboard.css';
import SubscriberStats from './SubscriberStats';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const buildDateRange = (dates) => {
  if (!dates || !dates[0] || !dates[1]) {
    return { startDate: null, endDate: null };
  }

  return {
    startDate: dates[0].format('YYYY-MM-DD'),
    endDate: dates[1].format('YYYY-MM-DD'),
  };
};

const Dashboard = () => {
  const today = dayjs();
  const navigate = useNavigate();
  const defaultRange = [dayjs().subtract(7, 'days'), dayjs()];
  const initialRange = buildDateRange(defaultRange);
  const [creditDateRange, setCreditDateRange] = useState(defaultRange);
  const [videoStatusDateRange, setVideoStatusDateRange] = useState(defaultRange);
  const [dailyVideoDateRange, setDailyVideoDateRange] = useState(defaultRange);
  const [typeDateRange, setTypeDateRange] = useState(defaultRange);
  const [typeStatusFilter, setTypeStatusFilter] = useState(null);
  const [costStatsDateRange, setCostStatsDateRange] = useState(defaultRange);
  const [toolUsageDateRange, setToolUsageDateRange] = useState(defaultRange);
  const [toolNameFilter, setToolNameFilter] = useState([]);
  
  // Basic Stats Query
  const { loading: basicStatsLoading, data: basicStatsData } = useQuery(getBasicStats);

  // Credit Stats Query with Date Range
  const { 
    loading: creditStatsLoading, 
    data: creditStatsData, 
    refetch: refetchCreditStats 
  } = useQuery(getCreditStats, {
    variables: {
      startDate: initialRange.startDate,
      endDate: initialRange.endDate
    },
    notifyOnNetworkStatusChange: true
  });

  // Daily Video Stats Query with Date Range
  const {
    loading: dailyVideoStatsLoading,
    data: dailyVideoStatsData,
    refetch: refetchDailyVideoStats
  } = useQuery(getDailyVideoStats, {
    variables: {
      startDate: initialRange.startDate,
      endDate: initialRange.endDate
    },
    notifyOnNetworkStatusChange: true
  });

  // Video Status Stats Query with Date Range
  const {
    loading: videoStatusStatsLoading,
    data: videoStatusStatsData,
    refetch: refetchVideoStatusStats
  } = useQuery(getVideoStatusStats, {
    variables: {
      startDate: initialRange.startDate,
      endDate: initialRange.endDate
    },
    notifyOnNetworkStatusChange: true
  });

  // Video Type Stats Query with Date Range and Status Filter
  const {
    loading: videoTypeStatsLoading,
    data: videoTypeStatsData,
    refetch: refetchVideoTypeStats
  } = useQuery(getVideoTypeStats, {
    variables: {
      startDate: initialRange.startDate,
      endDate: initialRange.endDate,
      status: null
    },
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: costStatsLoading,
    data: costStatsData,
    refetch: refetchCostStats
  } = useQuery(getVideoCostStats, {
    variables: {
      startDate: initialRange.startDate,
      endDate: initialRange.endDate
    },
    notifyOnNetworkStatusChange: true
  });

  const { data: getAllToolsData } = useQuery(getAllTools);

  const {
    loading: toolUsageLoading,
    data: toolUsageData,
    refetch: refetchToolUsage
  } = useQuery(getDailyToolUsage, {
    variables: {
      startDate: initialRange.startDate,
      endDate: initialRange.endDate,
      toolNames: toolNameFilter.length > 0 ? toolNameFilter : null
    },
    notifyOnNetworkStatusChange: true
  });

  const handleVideoStatusDateChange = async (dates) => {
    setVideoStatusDateRange(dates);
    const { startDate, endDate } = buildDateRange(dates);

    await refetchVideoStatusStats({ startDate, endDate });
  };

  const handleDailyVideoDateChange = async (dates) => {
    setDailyVideoDateRange(dates);
    const { startDate, endDate } = buildDateRange(dates);

    await refetchDailyVideoStats({ startDate, endDate });
  };

  const handleCostStatsDateChange = async (dates) => {
    setCostStatsDateRange(dates);
    const { startDate, endDate } = buildDateRange(dates);

    await refetchCostStats({ startDate, endDate });
  };

  const handleTypeDateChange = async (dates) => {
    setTypeDateRange(dates);
    const { startDate, endDate } = buildDateRange(dates);

    await refetchVideoTypeStats({
      startDate,
      endDate,
      status: typeStatusFilter
    });
  };

  const handleTypeStatusChange = async (status) => {
    setTypeStatusFilter(status);
    const { startDate, endDate } = buildDateRange(typeDateRange);

    await refetchVideoTypeStats({
      startDate,
      endDate,
      status: status || null
    });
  };

  const handleToolUsageDateChange = async (dates) => {
    setToolUsageDateRange(dates);

    const { startDate, endDate } = buildDateRange(dates);

    await refetchToolUsage({
      startDate,
      endDate,
      toolNames: toolNameFilter.length ? toolNameFilter : null
    });
  };

  const handleToolNameFilterChange = async (values) => {
    setToolNameFilter(values);

    const { startDate, endDate } = buildDateRange(toolUsageDateRange);

    await refetchToolUsage({
      startDate,
      endDate,
      toolNames: values.length ? values : null
    });
  };

  if (basicStatsLoading) return <Spin size="large" className="dashboard-loading" />;

  const basicStats = basicStatsData?.getBasicStats || {};
  const creditStats = creditStatsData?.getCreditStats || [];
  const dailyVideoStats = dailyVideoStatsData?.getDailyVideoStats || [];
  const videoStatusStats = videoStatusStatsData?.getVideoStatusStats || {};
  const videoTypeStats = videoTypeStatsData?.getVideoTypeStats || {};
  const toolUsageStats = toolUsageData?.getDailyToolUsage || [];

  const toolOptions = (getAllToolsData?.getAllTools?.tools ?? []).map(tool => ({
    label: tool.name,
    value: tool.name,
  }));

  // Transform credit stats data for Area chart
  const transformedCreditData = [];
  creditStats.forEach(stat => {
    const { __typename, ...creditCounts } = stat.creditCounts;
    Object.entries(creditCounts).forEach(([type, value]) => {
      transformedCreditData.push({
        date: stat.date,
        type: type.charAt(0).toUpperCase() + type.slice(1),
        value: value
      });
    });
  });

  // Transform tool usage data for stacked bar chart
  const transformedToolUsageData = [];
  toolUsageStats.forEach(stat => {
    stat.toolUsage.forEach(tool => {
      transformedToolUsageData.push({
        date: stat.date,
        toolName: tool.toolName,
        count: tool.count
      });
    });
  });

  // Credit Stats Area Chart Configuration
  const areaConfig = {
    data: transformedCreditData,
    xField: 'date',
    yField: 'value',
    seriesField: 'type',
    color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96'],
    areaStyle: {
      fillOpacity: 0.6,
    },
    legend: {
      position: 'top',
    },
    smooth: true,
    xAxis: {
      type: 'time',
      mask: 'MM/DD/YYYY',
    },
    tooltip: {
      shared: true,
      showCrosshairs: true,
    },
  };

  // Pie Chart Configuration
  const pieConfig = {
    data: [
      { type: 'Failed', value: videoStatusStats?.failed || 0 },
      { type: 'Running', value: videoStatusStats?.running || 0 },
      { type: 'In Queue', value: videoStatusStats?.in_queue || 0 },
      { type: 'Throttled', value: videoStatusStats?.throttled || 0 },
      { type: 'Succeeded', value: videoStatusStats?.succeeded || 0 },
      { type: 'Pending', value: videoStatusStats?.pending || 0 },
    ],
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    label: {
      type: 'outer',
      content: '{name} {percentage}',
    },
    interactions: [{ type: 'element-active' }],
    legend: {
      position: 'right',
    },
    color: ['#ff4d4f', '#faad14', '#1890ff', '#purple', '#52c41a', '#orange'],
  };

  // Line Chart Configuration
  const lineConfig = {
    data: dailyVideoStats,
    xField: 'date',
    yField: 'count',
    point: {
      size: 5,
      shape: 'diamond',
    },
    label: {
      style: {
        fill: '#aaa',
      },
    },
  };

  // Video Type Pie Chart Configuration
  const typeConfig = {
    data: [
      { type: 'Image to Video', value: videoTypeStats?.img2Video || 0 },
      { type: 'Text to Video', value: videoTypeStats?.text2Video || 0 },
      { type: 'Tool Predict', value: videoTypeStats?.toolPredict || 0 },
      { type: 'Story Board', value: videoTypeStats?.storyBoard || 0 },
      { type: 'Generation With Audio', value: videoTypeStats?.generationWithAudio || 0 },
      { type: 'Edit Tool', value: videoTypeStats?.editTool || 0 },
      { type: 'Credit', value: videoTypeStats?.credit || 0 },
      { type: 'Credit V2', value: videoTypeStats?.creditV2 || 0 },
      { type: 'Edit', value: videoTypeStats?.edit || 0 },
    ],
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    label: {
      type: 'outer',
      content: '{name} {percentage}',
    },
    interactions: [{ type: 'element-active' }],
    legend: {
      position: 'right',
    },
    color: ['#1890ff', '#52c41a', '#722ed1', '#faad14', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'],
  };

  // Tool Usage Stacked Bar Chart Configuration
  const toolUsageConfig = {
    data: transformedToolUsageData,
    xField: 'date',
    yField: 'count',
    seriesField: 'toolName',
    isStack: true,
    color: [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
      '#6C5CE7', '#A29BFE', '#FD79A8', '#FDCB6E', '#E17055',
      '#74B9FF', '#81ECEC', '#55EFC4', '#DFE6E9', '#00B894',
      '#00CEC9', '#0984E3', '#6C5CE7', '#B2BEC3', '#FF7675'
    ],
    legend: {
      position: 'top',
      maxRow: 2,
    },
    xAxis: {
      label: {
        autoRotate: false,
      },
    },
    tooltip: {
      shared: true,
      showCrosshairs: true,
    },
    interactions: [
      {
        type: 'active-region',
        enable: false,
      },
    ],
    columnStyle: {
      radius: [2, 2, 0, 0],
    },
  };

  const renderChart = (loading, chart) => {
    if (loading) {
      return (
        <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin />
        </div>
      );
    }
    return chart;
  };

  const statsCards = [
    {
      title: 'Total Users',
      value: basicStats.totalUsers || 0,
      icon: <UsergroupAddOutlined />,
      color: '#1890ff'
    },
    {
      title: 'Total Videos',
      value: basicStats.totalVideos || 0,
      icon: <PlaySquareOutlined />,
      color: '#52c41a'
    },
    {
      title: 'Active Subscriptions',
      value: basicStats.activeSubscriptions || 0,
      icon: <CrownOutlined />,
      color: '#faad14'
    },
    {
      title: 'Last Video',
      value: basicStats.lastVideoDate ? new Date(basicStats.lastVideoDate).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : '-',
      icon: <FieldTimeOutlined />,
      color: '#722ed1'
    }
  ];

  const videoTypeCards = [
    {
      title: 'Image to Video',
      value: videoTypeStats?.img2Video || 0,
      color: '#1890ff'
    },
    {
      title: 'Text to Video',
      value: videoTypeStats?.text2Video || 0,
      color: '#52c41a'
    },
    {
      title: 'Tool Predict',
      value: videoTypeStats?.toolPredict || 0,
      color: '#722ed1'
    },
    {
      title: 'Generation With Audio',
      value: videoTypeStats?.generationWithAudio || 0,
      color: '#FF6B6B',
      breakdown: {
        fromImage: videoTypeStats?.generationWithAudioFromImage || 0,
        fromText: videoTypeStats?.generationWithAudioFromText || 0
      }
    },
  ];

  // Use the total from backend for accuracy
  const total = videoTypeStats?.total || 0;

  // Aggregate tool usage data for the selected time period
  const aggregateToolUsage = () => {
    const toolMap = new Map();
    let totalUsage = 0;

    toolUsageStats.forEach(stat => {
      stat.toolUsage.forEach(tool => {
        const currentCount = toolMap.get(tool.toolName) || { count: 0, toolId: tool.toolId };
        toolMap.set(tool.toolName, {
          toolId: tool.toolId,
          count: currentCount.count + tool.count
        });
        totalUsage += tool.count;
      });
    });

    const sortedTools = Array.from(toolMap.entries())
      .map(([toolName, data], index) => ({
        key: index,
        rank: index + 1,
        toolName,
        toolId: data.toolId,
        count: data.count,
        percentage: totalUsage > 0 ? ((data.count / totalUsage) * 100).toFixed(2) : '0.00'
      }))
      .sort((a, b) => b.count - a.count)
      .map((item, index) => ({ ...item, rank: index + 1 }));

    return sortedTools;
  };

  const aggregatedToolData = aggregateToolUsage();
  const totalToolUsage = aggregatedToolData.reduce((sum, tool) => sum + tool.count, 0);

  // Aggregated tool usage table columns
  const aggregatedToolColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      align: 'center',
      render: (rank) => (
        <div style={{
          fontWeight: 'bold',
          fontSize: '16px',
          color: rank === 1 ? '#faad14' : rank === 2 ? '#8c8c8c' : rank === 3 ? '#d4a574' : '#595959'
        }}>
          {rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : rank}
        </div>
      )
    },
    {
      title: 'Tool Name',
      dataIndex: 'toolName',
      key: 'toolName',
      render: (toolName, record) => (
        <span
          style={{
            cursor: 'pointer',
            color: '#1890ff',
            fontWeight: '500',
            fontSize: '14px'
          }}
          onClick={() => navigate(`/tools/${record.toolId}`)}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {toolName}
        </span>
      )
    },
    {
      title: 'Total Usage',
      dataIndex: 'count',
      key: 'count',
      width: 150,
      sorter: (a, b) => a.count - b.count,
      render: (count) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong style={{ fontSize: '16px' }}>{count.toLocaleString()}</strong>
        </div>
      )
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      width: 180,
      sorter: (a, b) => parseFloat(a.percentage) - parseFloat(b.percentage),
      render: (percentage, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          <div style={{
            flex: 1,
            height: '20px',
            background: '#f0f0f0',
            borderRadius: '10px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              width: `${percentage}%`,
              height: '100%',
              background: record.rank === 1 ? '#52c41a' : 
                         record.rank === 2 ? '#1890ff' : 
                         record.rank === 3 ? '#faad14' : '#722ed1',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <span style={{ 
            minWidth: '50px', 
            textAlign: 'right',
            fontWeight: '500'
          }}>
            {percentage}%
          </span>
        </div>
      )
    }
  ];

  return (
    <div className="dashboard-container">
      {/* Basic Stats Cards */}
      <Row gutter={[16, 16]}>
        {statsCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card bodyStyle={{ padding: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                {React.cloneElement(stat.icon, { 
                  style: { fontSize: '20px', color: stat.color } 
                })}
                <span style={{ fontSize: '14px', opacity: 0.65 }}>
                  {stat.title}
                </span>
              </div>
              <div style={{ fontSize: '20px', fontWeight: '500' }}>
                {stat.value}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Video Cost Stats Section */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>💰 Credit Cost Statistics (Succeeded Videos Only)</span>
                <RangePicker 
                  onChange={handleCostStatsDateChange}
                  format="YYYY-MM-DD"
                  value={costStatsDateRange}
                  size="small"
                />
              </div>
            }
            bodyStyle={{ padding: '16px' }}
          >
            {costStatsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spin />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={4}>
                  <Card bodyStyle={{ padding: '12px', background: '#f0f5ff' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '12px', opacity: 0.65, marginBottom: '4px' }}>🛠️ Tool Videos</span>
                      <span style={{ fontSize: '24px', fontWeight: '600', color: '#1890ff' }}>
                        {costStatsData?.getVideoCostStats?.toolVideoCount || 0}
                      </span>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                  <Card bodyStyle={{ padding: '12px', background: '#f0f5ff' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '12px', opacity: 0.65, marginBottom: '4px' }}>🛠️ Tool Credits</span>
                      <span style={{ fontSize: '24px', fontWeight: '600', color: '#1890ff' }}>
                        {(costStatsData?.getVideoCostStats?.toolCreditsSpent || 0).toLocaleString()}
                      </span>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                  <Card bodyStyle={{ padding: '12px', background: '#f6ffed' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '12px', opacity: 0.65, marginBottom: '4px' }}>🎬 Normal Videos</span>
                      <span style={{ fontSize: '24px', fontWeight: '600', color: '#52c41a' }}>
                        {costStatsData?.getVideoCostStats?.normalVideoCount || 0}
                      </span>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                  <Card bodyStyle={{ padding: '12px', background: '#f6ffed' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '12px', opacity: 0.65, marginBottom: '4px' }}>🎬 Normal Credits</span>
                      <span style={{ fontSize: '24px', fontWeight: '600', color: '#52c41a' }}>
                        {(costStatsData?.getVideoCostStats?.normalCreditsSpent || 0).toLocaleString()}
                      </span>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                  <Card bodyStyle={{ padding: '12px', background: '#fff7e6' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '12px', opacity: 0.65, marginBottom: '4px' }}>📊 Total Videos</span>
                      <span style={{ fontSize: '24px', fontWeight: '600', color: '#fa8c16' }}>
                        {costStatsData?.getVideoCostStats?.totalVideoCount || 0}
                      </span>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                  <Card bodyStyle={{ padding: '12px', background: '#fff7e6' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '12px', opacity: 0.65, marginBottom: '4px' }}>📊 Total Credits</span>
                      <span style={{ fontSize: '24px', fontWeight: '600', color: '#fa8c16' }}>
                        {(costStatsData?.getVideoCostStats?.totalCreditsSpent || 0).toLocaleString()}
                      </span>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      {/* Video Type Stats Section */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Video Types</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Select
                    placeholder="All Statuses"
                    allowClear
                    style={{ width: 140 }}
                    size="small"
                    value={typeStatusFilter}
                    onChange={handleTypeStatusChange}
                    options={[
                      { value: 'SUCCEEDED', label: '✅ Succeeded' },
                      { value: 'FAILED', label: '❌ Failed' },
                      { value: 'RUNNING', label: '🔄 Running' },
                      { value: 'PENDING', label: '⏳ Pending' },
                      { value: 'IN_QUE', label: '📋 In Queue' },
                      { value: 'THROTTLED', label: '⚠️ Throttled' },
                      { value: 'MODERATION', label: '🚩 Moderation' },
                    ]}
                  />
                  <RangePicker 
                    onChange={handleTypeDateChange}
                    format="YYYY-MM-DD"
                    value={typeDateRange}
                    size="small"
                  />
                </div>
              </div>
            }
            bodyStyle={{ padding: '12px' }}
          >
            <Row gutter={[16, 16]}>
              {videoTypeCards.map((type, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <Card bodyStyle={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '14px', opacity: 0.65, marginBottom: '4px' }}>
                        {type.title}
                      </span>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontSize: '20px', fontWeight: '500', color: type.color }}>
                          {type.value}
                        </span>
                        <span style={{ fontSize: '12px', opacity: 0.45 }}>
                          {total > 0 && type.value > 0 ? `${((type.value / total) * 100).toFixed(1)}%` : '0%'}
                        </span>
                      </div>
                      {type.breakdown && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                            <span style={{ opacity: 0.65 }}>🖼️ From Image:</span>
                            <span style={{ fontWeight: '500', color: '#1890ff' }}>{type.breakdown.fromImage}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '4px' }}>
                            <span style={{ opacity: 0.65 }}>📝 From Text:</span>
                            <span style={{ fontWeight: '500', color: '#52c41a' }}>{type.breakdown.fromText}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="dashboard-charts">
        <Col xs={24}>
          <Card 
            title="Tool Usage Overview"
            extra={
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Filter tools"
                  style={{ minWidth: 220 }}
                  options={toolOptions}
                  value={toolNameFilter}
                  onChange={handleToolNameFilterChange}
                  maxTagCount={2} 
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                />
                <RangePicker 
                  onChange={handleToolUsageDateChange}
                  format="YYYY-MM-DD"
                  value={toolUsageDateRange}
                />
              </div>
            }
          >
            <Tabs defaultActiveKey="1">
              <TabPane tab="Chart View" key="1">
                {renderChart(toolUsageLoading, <Column {...toolUsageConfig} height={500} />)}
              </TabPane>
              <TabPane tab="List View" key="2">
                <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic 
                        title="Total Tools Used" 
                        value={aggregatedToolData.length}
                        prefix="🛠️"
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic 
                        title="Total Usage Count" 
                        value={totalToolUsage.toLocaleString()}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic 
                        title="Period" 
                        value={`${toolUsageDateRange?.[0]?.format('MMM DD')} - ${toolUsageDateRange?.[1]?.format('MMM DD')}`}
                        valueStyle={{ fontSize: '16px' }}
                      />
                    </Col>
                  </Row>
                </div>
                <Table
                  columns={aggregatedToolColumns}
                  dataSource={aggregatedToolData}
                  loading={toolUsageLoading}
                  pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} tools`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    defaultPageSize: 20
                  }}
                  scroll={{ x: 'max-content' }}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[24, 24]} className="dashboard-charts">
        <Col xs={24} lg={12}>
          <Card 
            title="Video Status Distribution"
            extra={
              <RangePicker 
                onChange={handleVideoStatusDateChange}
                format="YYYY-MM-DD"
                value={videoStatusDateRange}
              />
            }
          >
            {renderChart(videoStatusStatsLoading, <Pie {...pieConfig} />)}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Daily Video Count"
            extra={
              <RangePicker 
                onChange={handleDailyVideoDateChange}
                format="YYYY-MM-DD"
                value={dailyVideoDateRange}
              />
            }
          >
            {renderChart(dailyVideoStatsLoading, <Line {...lineConfig} />)}
          </Card>
        </Col>
      </Row>

      {/* Credit Stats Chart */}

      <SubscriberStats />
    </div>
  );
};

export default Dashboard; 